import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  StatTile,
  ViewButton,
} from '@/components/ui';
import { StudentSubscriptionsList } from '@/features/billing/StudentSubscriptionsList';
import {
  // formatAmounts, -- used by the hidden money tiles
  formatMoney,
  formatPrice,
  statusLabel,
  statusTone,
} from '@/features/billing/money';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

/**
 * The subscriber roster.
 *
 * Separate from the billing screen, which is the platform's position — what is
 * sold, what it earns, what is owed in total. This is the working list behind
 * it: one company per line, filterable by the questions actually asked of it
 * ("who is leaving?", "who owes us money?", "who is on scale?").
 *
 * A company on the free plan has no subscription row and so appears nowhere
 * here. That is the billing model rather than a gap — the company directory is
 * where every company lives, paying or not.
 *
 * Students are the second tab: a different customer on different rules (a
 * student plan starts when it is paid), so a different roster. The tab lives in
 * the URL (`?customer=student`) so a link from a student plan lands on it.
 */
const CUSTOMER_TABS = [
  { key: 'company', label: 'Companies' },
  { key: 'student', label: 'Students' },
];

/**
 * The roster's tabs. Each is the same rule as the figure above it, so a tab's
 * count and its tile always agree (`view` on the API):
 *   Paying           live and bought at a price -- "Paying companies"
 *   Payment overdue  has an unpaid invoice past its due date. A company keeps
 *                    working while an invoice is chased, so this comes from
 *                    invoices; the subscription itself still reads Active.
 *   Ended            cancelled or run out, kept for its billing history.
 * A Trial tab was here too; no plan offers a trial, so it is switched off:
 *   { key: 'TRIALING', label: 'Trial' },
 */
const VIEW_TABS = [
  { key: '', label: 'All', countKey: 'all' },
  { key: 'paying', label: 'Paying', countKey: 'paying' },
  { key: 'overdue', label: 'Payment overdue', countKey: 'overdue' },
  { key: 'ended', label: 'Ended', countKey: 'ended' },
];

/** What the selected tab holds, in a sentence, under the tabs. */
const VIEW_HINTS = {
  paying: 'Companies on a paid plan right now. Matches "Paying companies" above.',
  overdue:
    'Companies with an unpaid invoice past its due date. They keep their plan while the invoice is chased.',
  ended:
    'Subscriptions that were cancelled or ran out. The company is back on the free plan; the row is kept for its billing history.',
};

const PAGE_SIZE = 20;

export function AdminSubscriptionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [view, setView] = useState('');
  const [ending, setEnding] = useState('');
  const [page, setPage] = useState(1);
  const planId = searchParams.get('plan_id') ?? '';
  const customer = searchParams.get('customer') === 'student' ? 'student' : 'company';

  /** Rewrites the URL filters, keeping whichever of them is not being changed. */
  function setUrl({ nextCustomer = customer, nextPlan = planId }) {
    const next = {};
    if (nextCustomer === 'student') next.customer = 'student';
    if (nextPlan) next.plan_id = nextPlan;
    setSearchParams(next, { replace: true });
  }

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(view ? { view } : {}),
    ...(planId ? { plan_id: planId } : {}),
    ...(ending ? { ending: ending === 'yes' } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-subscriptions', params],
    queryFn: () => adminApi.listSubscriptions(params),
    // Keeps the filters mounted -- and focused -- while the next page loads.
    placeholderData: keepPreviousData,
    enabled: customer === 'company',
  });
  const plans = useQuery({ queryKey: ['admin-plans'], queryFn: adminApi.listBillingPlans });
  const overview = useQuery({
    queryKey: ['admin-billing-overview'],
    queryFn: adminApi.getBillingOverview,
  });

  /** Every filter change is a new result set, so page 1 is the only sane page. */
  function change(setter) {
    return (event) => {
      setter(event.target.value);
      setPage(1);
    };
  }

  function changePlan(event) {
    setUrl({ nextPlan: event.target.value });
    setPage(1);
  }

  // The plan lives in the URL (so a link from the plans screen lands filtered)
  // but it is a filter like the others, and Reset clears it with them.
  const activeCount = [search !== '', view !== '', planId !== '', ending !== ''].filter(
    Boolean,
  ).length;

  function resetFilters() {
    setSearch('');
    setView('');
    setEnding('');
    if (planId) setUrl({ nextPlan: '' });
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const totals = overview.data;
  const counts = totals?.subscription_counts ?? {};

  function openView(key) {
    setView(key);
    setPage(1);
  }

  return (
    <div>
      <PageHeader
        title="Subscriptions"
        description="Every paying company and student, what they are on, and what they owe."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <LinkButton to={paths.admin.plans} variant="secondary" size="sm">
              Plans
            </LinkButton>
            <LinkButton to={paths.admin.billing} variant="secondary" size="sm">
              Billing overview
            </LinkButton>
          </div>
        }
      />

      <SegmentedTabs
        label="Customer"
        className="mb-6"
        tabs={CUSTOMER_TABS}
        value={customer}
        // A plan belongs to one audience, so its filter does not carry over.
        onChange={(key) => setUrl({ nextCustomer: key, nextPlan: '' })}
      />

      {customer === 'student' ? (
        <>
          {totals && (
            <div className="mb-6 grid gap-4 sm:grid-cols-3">
              <StatTile
                label="Paying students"
                value={totals.paying_students ?? 0}
                hint="On a running paid plan"
              />
              {/* Money figures, hidden: this page counts customers; the
                  amounts are on the Billing overview.
              <StatTile
                label="Monthly recurring"
                value={formatAmounts(totals.student_mrr_by_currency, 'INR')}
                hint="Per currency · yearly plans divided down to a month"
              />
              <StatTile
                label="Outstanding (students)"
                value={formatAmounts(totals.student_outstanding_by_currency, 'INR')}
                hint={
                  totals.overdue_students
                    ? `${totals.overdue_students} ${totals.overdue_students === 1 ? 'student' : 'students'} overdue`
                    : 'Nothing overdue'
                }
              />
              */}
              <StatTile
                label="Outstanding students"
                value={totals.students_owing ?? 0}
                hint="With an unpaid invoice"
              />
              <StatTile
                label="Overdue students"
                value={totals.overdue_students ?? 0}
                hint="With an invoice past its due date"
              />
            </div>
          )}
          <StudentSubscriptionsList
            planId={planId}
            onPlanChange={(value) => setUrl({ nextPlan: value })}
          />
        </>
      ) : (
        <>
          {overview.isError && (
            <div className="mb-6">
              <ErrorState
                title="Couldn't load the billing totals"
                message={errorMessage(overview.error)}
                onRetry={() => void overview.refetch()}
              />
            </div>
          )}

          {totals && (
            <div className="mb-6 grid gap-4 sm:grid-cols-3">
              <TileButton onClick={() => openView('paying')} title="Show the paying companies">
                <StatTile
                  label="Paying companies"
                  value={totals.paying_companies}
                  hint="Open the Paying tab"
                />
              </TileButton>
              {/* Money figures, hidden: this page counts customers; the
                  amounts are on the Billing overview.
              <StatTile
                label="Monthly recurring"
                value={formatAmounts(totals.company_mrr_by_currency, 'EUR')}
                hint="Per currency · yearly plans divided down to a month"
              />
              <TileButton
                onClick={() => openView('overdue')}
                title="Show the companies with an overdue invoice"
              >
                <StatTile
                  label="Outstanding (companies)"
                  value={formatAmounts(totals.company_outstanding_by_currency, 'EUR')}
                  hint={
                    totals.overdue_companies
                      ? `${totals.overdue_companies} ${totals.overdue_companies === 1 ? 'company' : 'companies'} overdue`
                      : 'Nothing overdue'
                  }
                />
              </TileButton>
              */}
              <StatTile
                label="Outstanding companies"
                value={totals.companies_owing ?? 0}
                hint="With an unpaid invoice"
              />
              <TileButton
                onClick={() => openView('overdue')}
                title="Show the companies with an overdue invoice"
              >
                <StatTile
                  label="Overdue companies"
                  value={totals.overdue_companies ?? 0}
                  hint="Open the Payment overdue tab"
                />
              </TileButton>
            </div>
          )}

          {plans.isError && (
            <Alert tone="error" title="Couldn't load the plan list" className="mb-6">
              <p>{errorMessage(plans.error)} The plan filter is empty until it loads.</p>
              <Button
                variant="secondary"
                size="xs"
                className="mt-2"
                onClick={() => void plans.refetch()}
              >
                Try again
              </Button>
            </Alert>
          )}

          <ListCard
            isRefreshing={isPlaceholderData}
            toolbarStart={
              <SegmentedTabs
                variant="plain"
                label="Subscriptions"
                tabs={VIEW_TABS.map(({ countKey, ...tab }) => ({
                  ...tab,
                  count: counts[countKey],
                }))}
                value={view}
                onChange={openView}
              />
            }
            toolbarEnd={
              <>
                <Select
                  label="Plan"
                  hideLabel
                  size="sm"
                  wrapperClassName="w-full sm:w-44"
                  value={planId}
                  onChange={changePlan}
                >
                  <option value="">All plans</option>
                  {(plans.data ?? [])
                    .filter((plan) => plan.audience !== 'STUDENT')
                    .map((plan) => (
                      <option key={plan.code} value={plan.id}>
                        {plan.name}
                      </option>
                    ))}
                </Select>
                {/* A pending cancellation and a pending downgrade are the same thing
                to whoever is chasing renewals: a plan about to be left. */}
                <Select
                  label="Ending"
                  hideLabel
                  size="sm"
                  wrapperClassName="w-full sm:w-44"
                  value={ending}
                  onChange={change(setEnding)}
                >
                  <option value="">All subscriptions</option>
                  <option value="yes">Ending or downgrading</option>
                  <option value="no">Continuing</option>
                </Select>
                <SearchInput
                  label="Search subscriptions"
                  hideLabel
                  size="sm"
                  className="w-full sm:w-64"
                  placeholder="Company name or slug"
                  value={search}
                  onChange={change(setSearch)}
                  onClear={() => {
                    setSearch('');
                    setPage(1);
                  }}
                />
                {activeCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={resetFilters}>
                    <Icon name="reset" size={14} />
                    Reset
                  </Button>
                )}
              </>
            }
            state={
              isError ? (
                <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
              ) : isPending ? (
                <LoadingState label="Loading subscriptions…" />
              ) : rows.length === 0 ? (
                activeCount > 0 ? (
                  <EmptyState
                    title={
                      view === 'overdue'
                        ? 'Nobody is overdue'
                        : view === 'ended'
                          ? 'Nothing has ended'
                          : 'No subscriptions'
                    }
                    description={
                      VIEW_HINTS[view] ??
                      'Nothing matches these filters. Companies on the free plan have no subscription and never appear here.'
                    }
                    action={
                      <Button variant="secondary" size="sm" onClick={resetFilters}>
                        Reset filters
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    title="No subscriptions yet"
                    description="Paying companies appear here. Companies on the free plan have no subscription and never appear here."
                  />
                )
              ) : null
            }
            footer={
              meta && (
                <Pagination
                  className=""
                  page={meta.page}
                  pageSize={PAGE_SIZE}
                  totalPages={meta.total_pages}
                  totalItems={meta.total_items}
                  onPageChange={setPage}
                />
              )
            }
          >
            {VIEW_HINTS[view] && rows.length > 0 && (
              <p className="text-ink-muted mb-3 flex items-start gap-2 text-xs">
                <Icon name="info" size={14} className="mt-px shrink-0" />
                {VIEW_HINTS[view]}
              </p>
            )}
            <BoxList>
              {rows.map((row) => (
                <BoxRow
                  key={row.id}
                  accent={view === 'overdue' ? 'warning' : statusTone(row.status)}
                  leading={<BoxAvatar name={row.company_name} />}
                  title={row.company_name}
                  to={paths.admin.subscriptionDetail(row.id)}
                  badges={
                    <>
                      <Badge dot tone={statusTone(row.status)}>
                        {statusLabel(row.status)}
                      </Badge>
                      {row.cancel_at_period_end && <Badge tone="warning">Ending</Badge>}
                    </>
                  }
                  subtitle={
                    <Link
                      to={paths.admin.companyDetail(row.company_id)}
                      className="hover:underline"
                    >
                      {row.company_slug}
                    </Link>
                  }
                  facts={[
                    {
                      icon: 'package',
                      tone: 'brand',
                      text: `${row.plan_name} · ${formatPrice(row.price_cents, row.currency, row.interval)}`,
                    },
                    row.scheduled_plan_name && {
                      icon: 'arrowRight',
                      text: `Moving to ${row.scheduled_plan_name}`,
                    },
                    { icon: 'calendar', text: `Renews ${formatDate(row.current_period_end)}` },
                    row.outstanding_cents > 0
                      ? {
                          icon: 'banknote',
                          tone: 'warning',
                          text: `${formatMoney(row.outstanding_cents, row.currency)} outstanding · ${row.open_invoices} open`,
                        }
                      : { icon: 'banknote', text: 'Nothing outstanding' },
                  ]}
                  meta={
                    <span className="text-ink-muted text-xs">
                      Subscribed {formatDate(row.created_at)}
                    </span>
                  }
                  actions={
                    <ViewButton
                      to={paths.admin.subscriptionDetail(row.id)}
                      title={`Manage ${row.company_name}'s subscription`}
                      label="Manage subscription"
                      icon="settings"
                    />
                  }
                />
              ))}
            </BoxList>
          </ListCard>
        </>
      )}
    </div>
  );
}

/** A figure that opens the tab it counts. */
function TileButton({ onClick, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="focus-visible:ring-brand-300 rounded-card block w-full text-left transition hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:outline-none"
    >
      {children}
    </button>
  );
}
